
import { useState } from "react";
import { useParams } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Upload, Loader2, FileUp } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { Progress } from "@/components/ui/progress";
import { useTranslation } from "react-i18next";

interface DocumentUploadProps {
  onUploadComplete?: () => void;
}

export const DocumentUpload = ({ onUploadComplete }: DocumentUploadProps) => {
  const { tenderId } = useParams<{ tenderId: string }>();
  const { toast } = useToast();
  const { t } = useTranslation('offerManagement');
  const [isUploading, setIsUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{[key: string]: number}>({});
  const [totalFiles, setTotalFiles] = useState(0);
  const [completedFiles, setCompletedFiles] = useState(0);

  const handleFileUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files || files.length === 0 || !tenderId) return;

    // Get the current user's ID
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) {
      toast({
        title: t("errorMessages.authRequired"),
        description: t("errorMessages.signInToUpload"),
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    setTotalFiles(files.length);
    setCompletedFiles(0);
    setUploadProgress({});
    
    const newUploadProgress: {[key: string]: number} = {};
    Array.from(files).forEach(file => {
      newUploadProgress[file.name] = 0;
    });
    setUploadProgress(newUploadProgress);

    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        
        // Generate a unique file path that includes the user ID
        const fileExt = file.name.split('.').pop();
        const fileName = `${user.id}/${crypto.randomUUID()}.${fileExt}`;

        // Upload file to Supabase Storage
        const { error: uploadError } = await supabase.storage
          .from('tender_documents')
          .upload(fileName, file, {
            cacheControl: '3600',
            upsert: false
          });

        if (uploadError) throw uploadError;

        // Update progress manually since onUploadProgress is not available
        setUploadProgress(prev => ({
          ...prev,
          [file.name]: 100
        }));

        // Save file metadata to the database
        const { error: dbError } = await supabase
          .from('tender_documents')
          .insert({
            tender_id: tenderId,
            user_id: user.id,
            file_name: file.name,
            file_path: fileName,
            file_type: file.type,
            file_size: file.size,
          });

        if (dbError) throw dbError;

        setCompletedFiles(prev => prev + 1);
      }

      toast({
        title: t("common.success"),
        description: `${files.length} ${files.length > 1 ? t("documents.documentsUploaded") : t("documents.documentUploaded")}`,
      });

      // Reset the file input
      event.target.value = '';
      
      // Signal that upload is complete
      if (onUploadComplete) {
        onUploadComplete();
      }
      
    } catch (error) {
      console.error('Upload error:', error);
      toast({
        title: t("errorMessages.uploadFailed"),
        description: t("errorMessages.uploadFailedDesc"),
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
      setUploadProgress({});
      setTotalFiles(0);
      setCompletedFiles(0);
    }
  };

  const getTotalProgress = () => {
    if (totalFiles === 0) return 0;
    return (completedFiles / totalFiles) * 100;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("documents.documentUpload")}</CardTitle>
        <CardDescription>
          {t("documents.uploadDescription")}
        </CardDescription>
      </CardHeader>
      <CardContent>
        <div className="relative">
          <input
            type="file"
            onChange={handleFileUpload}
            className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
            disabled={isUploading}
            multiple
          />
          <Button 
            className="w-full h-32 border-dashed border-2" 
            variant="outline"
            disabled={isUploading}
          >
            <div className="flex flex-col items-center">
              {isUploading ? (
                <>
                  <Loader2 className="h-8 w-8 mb-2 animate-spin" />
                  <span>{t("documents.uploading")} {completedFiles}/{totalFiles} {t("documents.files")}</span>
                  <div className="w-full mt-2">
                    <Progress value={getTotalProgress()} className="h-2" />
                  </div>
                </>
              ) : (
                <>
                  <FileUp className="h-8 w-8 mb-2" />
                  <span>{t("documents.dragDrop")}</span>
                </>
              )}
            </div>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
